import React, { useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import {
  formatCnpj,
  formatCpf,
  isValidCnpj,
  isValidCpf,
  isValidEmail,
  isValidPhone,
  isValidUrl,
  normalizeCnpj,
  normalizeCpf,
} from '../common/validators';

interface GestorResponsavel {
  cpf: string;
  rg: string;
  nome_completo: string;
  data_nascimento: string;
  endereco: string;
  cargo: string;
  celular: string;
  whatsapp: string;
  email: string;
}

interface Creche {
  id: number;
  nome: string;
  nome_fantasia: string;
  cnpj: string;
  endereco: string;
  cep: string;
  telefone: string;
  celular: string;
  whatsapp: string;
  email: string;
  capacidade_instalada: number;
  // Horários de funcionamento por turno (usados no Contrato de Prestação de Serviços)
  horario_matutino_inicio: string;
  horario_matutino_fim: string;
  horario_vespertino_inicio: string;
  horario_vespertino_fim: string;
  horario_integral_inicio: string;
  horario_integral_fim: string;
  instagram: string;
  facebook: string;
  x_url: string;
  site_url: string;
  gestores: GestorResponsavel[];
  // Novos campos: Estrutura Física
  num_salas: number;
  tem_bercario: boolean;
  num_bercarios: number;
  area_total_m2: number;
  capacidade_por_sala: string;
  // Novos campos: Recursos Humanos (quantitativo)
  num_educadores: number;
  num_merendeiras: number;
  num_faxineiras: number;
  num_servicos_gerais: number;
  num_administrativo: number;
  // Configurações de RH (Cargos e Salários)
  cargos_salarios: Array<{ cargo: string; salario_base: number }>;
  // Status de aprovação (enum real da CrecheEntity; alterado no fluxo de aprovação SaaS)
  status: 'pendente' | 'ativa' | 'suspensa' | 'negada';
  plano_id: number;
  contrato_assinado: boolean;
  data_formalizacao: string;
}

interface CnpjApiResponse {
  razao_social?: string;
  nome_fantasia?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  cep?: string;
  ddd_telefone_1?: string;
  ddd_telefone_2?: string;
  email?: string;
}

function novoGestor(): GestorResponsavel {
  return {
    cpf: '',
    rg: '',
    nome_completo: '',
    data_nascimento: '',
    endereco: '',
    cargo: '',
    celular: '',
    whatsapp: '',
    email: '',
  };
}

export default function CrechesPage() {
  const [cnpjBuscaErro, setCnpjBuscaErro] = useState('');
  const [cnpjBuscando, setCnpjBuscando] = useState(false);

  async function consultarCnpj(item: Partial<Creche>, onChange: (campo: keyof Creche, valor: any) => void) {
    const cnpj = normalizeCnpj(item.cnpj || '');
    setCnpjBuscaErro('');

    if (!isValidCnpj(cnpj)) {
      setCnpjBuscaErro('CNPJ inválido para consulta.');
      return;
    }

    setCnpjBuscando(true);
    try {
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      if (!response.ok) {
        setCnpjBuscaErro('CNPJ não encontrado na base pública.');
        return;
      }

      const data = (await response.json()) as CnpjApiResponse;
      const enderecoParts = [
        data.logradouro,
        data.numero,
        data.bairro,
        data.municipio,
        data.uf,
      ].filter(Boolean);

      onChange('cnpj', formatCnpj(cnpj));
      onChange('nome', data.razao_social || item.nome || '');
      onChange('nome_fantasia', data.nome_fantasia || item.nome_fantasia || '');
      onChange('endereco', enderecoParts.join(', '));
      onChange('cep', data.cep || item.cep || '');
      onChange('telefone', data.ddd_telefone_1 || item.telefone || '');
      onChange('celular', data.ddd_telefone_2 || item.celular || '');
      onChange('email', data.email || item.email || '');
    } catch {
      setCnpjBuscaErro('Falha ao consultar CNPJ. Verifique conexão e tente novamente.');
    } finally {
      setCnpjBuscando(false);
    }
  }

  async function validarCreche(item: Partial<Creche>) {
    const cnpj = normalizeCnpj(item.cnpj || '');
    if (!cnpj || !isValidCnpj(cnpj)) {
      return 'CNPJ inválido. O cadastro de PJ exige CNPJ válido.';
    }

    try {
      const check = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      if (!check.ok) {
        return 'CNPJ inexistente na base pública. Não é permitido cadastrar PJ sem CNPJ válido.';
      }
    } catch {
      return 'Não foi possível validar o CNPJ online agora. Tente novamente.';
    }

    // Campos que HOJE PERSISTEM (colunas reais da CrecheEntity): validação obrigatória.
    if (!item.telefone || !isValidPhone(item.telefone, 'landline')) {
      return 'Telefone é obrigatório e deve conter DDD + número válido.';
    }
    if (!item.email || !isValidEmail(item.email)) {
      return 'E-mail da creche é obrigatório e deve ser válido.';
    }
    if (!item.site_url || !isValidUrl(item.site_url)) {
      return 'Endereço do site é obrigatório e deve começar com http:// ou https://.';
    }

    // Campos opcionais: não bloqueiam o cadastro — só validamos o formato quando preenchidos.
    if ((item.celular || '').trim() && !isValidPhone(item.celular as string, 'mobile')) {
      return 'Celular inválido (DDD + número).';
    }
    if ((item.whatsapp || '').trim() && !isValidPhone(item.whatsapp as string, 'mobile')) {
      return 'WhatsApp inválido (DDD + número).';
    }

    const gestores = item.gestores || [];
    for (let i = 0; i < gestores.length; i += 1) {
      const g = gestores[i];
      const ordem = i + 1;
      // Gestores são opcionais: valida só o que foi informado, sem exigir preenchimento.
      if ((g.cpf || '').trim() && !isValidCpf(normalizeCpf(g.cpf))) {
        return `CPF inválido no gestor ${ordem}.`;
      }
      if ((g.email || '').trim() && !isValidEmail(g.email)) {
        return `E-mail inválido no gestor ${ordem}.`;
      }
    }

    return null;
  }

  function renderGestores(item: Partial<Creche>, onChange: (campo: keyof Creche, valor: any) => void) {
    const gestores = item.gestores || [];

    function updateGestor(index: number, field: keyof GestorResponsavel, value: string) {
      const next = [...gestores];
      next[index] = { ...next[index], [field]: value };
      onChange('gestores', next);
    }

    function addGestor() {
      onChange('gestores', [...gestores, novoGestor()]);
    }

    function removeGestor(index: number) {
      const next = gestores.filter((_, i) => i !== index);
      onChange('gestores', next);
    }

    return (
      <div className="bloco-gestores">
        <div className="bloco-gestores-topo">
          <h4>Gestores Sócios Responsáveis</h4>
          <button type="button" className="btn-secundario" onClick={addGestor}>+ Adicionar gestor</button>
        </div>

        {gestores.length === 0 && <p className="texto-ajuda">Nenhum gestor incluído.</p>}

        {gestores.map((gestor, index) => {
          const cpfValido = isValidCpf(normalizeCpf(gestor.cpf || ''));
          const camposBloqueados = !cpfValido;
          return (
            <div className="card-gestor" key={`${index}-${gestor.cpf}`}>
              <div className="card-gestor-topo">
                <strong>Gestor {index + 1}</strong>
                <button type="button" className="btn-acao btn-perigo" onClick={() => removeGestor(index)}>🗑️</button>
              </div>

              <div className="linha-campos linha-2">
                <label>CPF
                  <input
                    value={gestor.cpf || ''}
                    onChange={(e) => updateGestor(index, 'cpf', formatCpf(e.target.value))}
                    placeholder="000.000.000-00"
                  />
                </label>
                <label>RG
                  <input disabled={camposBloqueados} value={gestor.rg || ''} onChange={(e) => updateGestor(index, 'rg', e.target.value)} />
                </label>
              </div>

              {!cpfValido && (gestor.cpf || '').length > 0 && (
                <p className="erro-campo">CPF inválido. Corrija para liberar os demais dados do gestor.</p>
              )}

              <div className="linha-campos linha-2">
                <label>Nome completo
                  <input disabled={camposBloqueados} value={gestor.nome_completo || ''} onChange={(e) => updateGestor(index, 'nome_completo', e.target.value)} />
                </label>
                <label>Data de nascimento
                  <input disabled={camposBloqueados} type="date" value={gestor.data_nascimento || ''} onChange={(e) => updateGestor(index, 'data_nascimento', e.target.value)} />
                </label>
              </div>

              <label>Endereço
                <input disabled={camposBloqueados} value={gestor.endereco || ''} onChange={(e) => updateGestor(index, 'endereco', e.target.value)} />
              </label>

              <div className="linha-campos linha-3">
                <label>Cargo
                  <input disabled={camposBloqueados} value={gestor.cargo || ''} onChange={(e) => updateGestor(index, 'cargo', e.target.value)} />
                </label>
                <label>Celular
                  <input disabled={camposBloqueados} value={gestor.celular || ''} onChange={(e) => updateGestor(index, 'celular', e.target.value)} placeholder="DDD + número" />
                </label>
                <label>WhatsApp
                  <input disabled={camposBloqueados} value={gestor.whatsapp || ''} onChange={(e) => updateGestor(index, 'whatsapp', e.target.value)} placeholder="DDD + número" />
                </label>
              </div>

              <label>E-mail
                <input disabled={camposBloqueados} type="email" value={gestor.email || ''} onChange={(e) => updateGestor(index, 'email', e.target.value)} />
              </label>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <TabelaCrud<Creche>
      titulo="Creches"
      endpoint="/creches"
      colunas={[
        { chave: 'id',          label: 'ID' },
        { chave: 'cnpj',        label: 'CNPJ' },
        { chave: 'nome',        label: 'Nome' },
        { chave: 'email',       label: 'E-mail' },
        { chave: 'endereco',    label: 'Endereço' },
        { chave: 'telefone', label: 'Telefone' },
        { chave: 'celular',     label: 'Celular' },
        { 
          chave: 'status',      
          label: 'Status',
          render: (item) => {
            const statusConfig: any = {
              'pendente': { color: '#1976D2', bg: '#E3F2FD', label: 'Pendente' },
              'ativa': { color: '#388E3C', bg: '#E8F5E9', label: 'Ativa' },
              'suspensa': { color: '#D32F2F', bg: '#FFEBEE', label: 'Suspensa' },
              'negada': { color: '#616161', bg: '#F5F5F5', label: 'Negada' },
            };
            const config = statusConfig[item.status || 'pendente'] || statusConfig.pendente;
            return (
              <span style={{ 
                padding: '4px 10px', 
                borderRadius: '20px', 
                fontSize: '0.8rem', 
                fontWeight: 'bold',
                color: config.color,
                backgroundColor: config.bg,
                border: `1px solid ${config.color}33`
              }}>
                {config.label}
              </span>
            );
          }
        },
        { chave: 'capacidade_instalada',  label: 'Capacidade' },
      ]}
      itemVazio={{
        nome: '',
        nome_fantasia: '',
        cnpj: '',
        endereco: '',
        cep: '',
        telefone: '',
        celular: '',
        whatsapp: '',
        email: '',
        capacidade_instalada: 0,
        horario_matutino_inicio: '07:30',
        horario_matutino_fim: '11:30',
        horario_vespertino_inicio: '13:30',
        horario_vespertino_fim: '17:30',
        horario_integral_inicio: '07:30',
        horario_integral_fim: '18:00',
        instagram: '',
        facebook: '',
        x_url: '',
        site_url: '',
        gestores: [novoGestor()],
        num_salas: 0,
        tem_bercario: false,
        num_bercarios: 0,
        area_total_m2: 0,
        capacidade_por_sala: '',
        num_educadores: 0,
        num_merendeiras: 0,
        num_faxineiras: 0,
        num_servicos_gerais: 0,
        num_administrativo: 0,
        cargos_salarios: [
          { cargo: 'Professor(a)', salario_base: 0 },
          { cargo: 'Monitor(a)', salario_base: 0 },
        ],
        status: 'pendente',
        plano_id: 0,
        contrato_assinado: false,
        data_formalizacao: '',
      }}
      validateItem={validarCreche}
      renderFormulario={(item, onChange) => (
        <>
          <h4 className="form-secao-titulo">Dados da Pessoa Jurídica</h4>

          <div className="linha-campos linha-2">
            <label>CNPJ (obrigatório)
              <input
                value={item.cnpj ?? ''}
                onChange={(e) => onChange('cnpj', formatCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
              />
            </label>
            <div className="campo-busca-cnpj">
              <button
                type="button"
                className="btn-secundario"
                onClick={() => consultarCnpj(item, onChange)}
                disabled={cnpjBuscando}
              >
                {cnpjBuscando ? 'Buscando CNPJ...' : 'Buscar dados por CNPJ'}
              </button>
              {cnpjBuscaErro && <span className="erro-campo">{cnpjBuscaErro}</span>}
            </div>
          </div>

          <div className="linha-campos linha-2">
            <label>Razão social
              <input value={item.nome ?? ''} onChange={(e) => onChange('nome', e.target.value)} />
            </label>
            <label>Nome fantasia
              <input value={item.nome_fantasia ?? ''} onChange={(e) => onChange('nome_fantasia', e.target.value)} />
            </label>
          </div>

          <div className="linha-campos linha-2">
            <label>Endereço completo
              <input value={item.endereco ?? ''} onChange={(e) => onChange('endereco', e.target.value)} />
            </label>
            <label>CEP
              <input value={item.cep ?? ''} onChange={(e) => onChange('cep', e.target.value)} />
            </label>
          </div>

          <div className="linha-campos linha-3">
            <label>Telefone (obrigatório)
              <input value={item.telefone ?? ''} onChange={(e) => onChange('telefone', e.target.value)} placeholder="DDD + número" />
            </label>
            <label>Celular
              <input value={item.celular ?? ''} onChange={(e) => onChange('celular', e.target.value)} placeholder="DDD + número" />
            </label>
            <label>WhatsApp
              <input value={item.whatsapp ?? ''} onChange={(e) => onChange('whatsapp', e.target.value)} placeholder="DDD + número" />
            </label>
          </div>

          <div className="linha-campos linha-2">
            <label>E-mail (obrigatório)
              <input type="email" value={item.email ?? ''} onChange={(e) => onChange('email', e.target.value)} />
            </label>
            <label>Capacidade instalada
              <input
                type="number"
                min={1}
                value={item.capacidade_instalada ?? 0}
                onChange={(e) => onChange('capacidade_instalada', Number(e.target.value))}
              />
            </label>
          </div>

          <h4 className="form-secao-titulo">Horários de Funcionamento</h4>
          <p className="texto-ajuda">Usados no Contrato de Prestação de Serviços gerado em Crianças.</p>
          <div className="linha-campos linha-2">
            <label>Turno matutino — início
              <input type="time" value={item.horario_matutino_inicio ?? '07:30'} onChange={(e) => onChange('horario_matutino_inicio', e.target.value)} />
            </label>
            <label>Turno matutino — fim
              <input type="time" value={item.horario_matutino_fim ?? '11:30'} onChange={(e) => onChange('horario_matutino_fim', e.target.value)} />
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>Turno vespertino — início
              <input type="time" value={item.horario_vespertino_inicio ?? '13:30'} onChange={(e) => onChange('horario_vespertino_inicio', e.target.value)} />
            </label>
            <label>Turno vespertino — fim
              <input type="time" value={item.horario_vespertino_fim ?? '17:30'} onChange={(e) => onChange('horario_vespertino_fim', e.target.value)} />
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>Turno integral — início
              <input type="time" value={item.horario_integral_inicio ?? '07:30'} onChange={(e) => onChange('horario_integral_inicio', e.target.value)} />
            </label>
            <label>Turno integral — fim
              <input type="time" value={item.horario_integral_fim ?? '18:00'} onChange={(e) => onChange('horario_integral_fim', e.target.value)} />
            </label>
          </div>

          <h4 className="form-secao-titulo">Redes Sociais e Site</h4>
          <div className="linha-campos linha-2">
            <label>Instagram
              <input value={item.instagram ?? ''} onChange={(e) => onChange('instagram', e.target.value)} placeholder="https://instagram.com/..." />
            </label>
            <label>Facebook
              <input value={item.facebook ?? ''} onChange={(e) => onChange('facebook', e.target.value)} placeholder="https://facebook.com/..." />
            </label>
          </div>

          <div className="linha-campos linha-2">
            <label>X (Twitter)
              <input value={item.x_url ?? ''} onChange={(e) => onChange('x_url', e.target.value)} placeholder="https://x.com/..." />
            </label>
            <label>Site oficial (obrigatório)
              <input value={item.site_url ?? ''} onChange={(e) => onChange('site_url', e.target.value)} placeholder="https://www.seusite.com.br" />
            </label>
          </div>

          <h4 className="form-secao-titulo">Gestores Sócios Responsáveis</h4>
          <p className="texto-ajuda">Dados pessoais dos sócios são tratados conforme a LGPD (CPF/RG mascarados na auditoria).</p>
          {renderGestores(item, onChange)}

          <h4 className="form-secao-titulo">Estrutura Física e Capacidade Instalada</h4>
          <div className="linha-campos linha-3">
            <label>Nº de Salas de Aula
              <input type="number" value={item.num_salas ?? 0} onChange={(e) => onChange('num_salas', Number(e.target.value))} />
            </label>
            <label>Área Total (m²)
              <input type="number" value={item.area_total_m2 ?? 0} onChange={(e) => onChange('area_total_m2', Number(e.target.value))} />
            </label>
            <label>Possui Berçário?
              <select
                value={item.tem_bercario ? 'sim' : 'nao'}
                onChange={(e) => {
                  const tem = e.target.value === 'sim';
                  onChange('tem_bercario', tem);
                  if (!tem) onChange('num_bercarios', 0);
                }}
              >
                <option value="nao">Não</option>
                <option value="sim">Sim</option>
              </select>
            </label>
          </div>

          {item.tem_bercario && (
            <div className="linha-campos linha-2">
              <label>Nº de Ambientes de Berçário
                <input type="number" value={item.num_bercarios ?? 0} onChange={(e) => onChange('num_bercarios', Number(e.target.value))} />
              </label>
              <label>Capacidade instalada (Total de Alunos)
                <input type="number" value={item.capacidade_instalada ?? 0} onChange={(e) => onChange('capacidade_instalada', Number(e.target.value))} />
              </label>
            </div>
          )}

          <label>Detalhamento da Capacidade (ex: Sala 1: 15, Sala 2: 20...)
            <textarea 
              value={item.capacidade_por_sala ?? ''} 
              onChange={(e) => onChange('capacidade_por_sala', e.target.value)}
              placeholder="Descreva a capacidade de cada ambiente..."
              style={{ minHeight: '80px', width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </label>

          <h4 className="form-secao-titulo">Estrutura Administrativa e Quadro de RH</h4>
          <p className="texto-ajuda" style={{ marginBottom: '10px' }}>Informe a quantidade atual de profissionais em cada categoria.</p>
          <div className="linha-campos linha-3">
            <label>Educadores/Professores
              <input type="number" value={item.num_educadores ?? 0} onChange={(e) => onChange('num_educadores', Number(e.target.value))} />
            </label>
            <label>Merendeiras/Cozinha
              <input type="number" value={item.num_merendeiras ?? 0} onChange={(e) => onChange('num_merendeiras', Number(e.target.value))} />
            </label>
            <label>Auxiliares de Limpeza
              <input type="number" value={item.num_faxineiras ?? 0} onChange={(e) => onChange('num_faxineiras', Number(e.target.value))} />
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>Serviços Gerais/Manutenção
              <input type="number" value={item.num_servicos_gerais ?? 0} onChange={(e) => onChange('num_servicos_gerais', Number(e.target.value))} />
            </label>
            <label>Administrativo/Secretaria
              <input type="number" value={item.num_administrativo ?? 0} onChange={(e) => onChange('num_administrativo', Number(e.target.value))} />
            </label>
          </div>

          <h4 className="form-secao-titulo">Configuração de Cargos e Salários Base</h4>
          <div className="bloco-lista-simples">
            { (item.cargos_salarios || []).map((cs, idx) => (
              <div key={idx} className="linha-campos linha-2" style={{ marginBottom: '8px', alignItems: 'flex-end' }}>
                <label>Cargo
                  <input value={cs.cargo} onChange={(e) => {
                    const next = [...(item.cargos_salarios || [])];
                    next[idx].cargo = e.target.value;
                    onChange('cargos_salarios', next);
                  }} />
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <label style={{ flex: 1 }}>Salário Base (R$)
                    <input type="number" value={cs.salario_base} onChange={(e) => {
                      const next = [...(item.cargos_salarios || [])];
                      next[idx].salario_base = Number(e.target.value);
                      onChange('cargos_salarios', next);
                    }} />
                  </label>
                  <button 
                    type="button" 
                    className="btn-acao btn-perigo" 
                    style={{ marginBottom: '4px' }}
                    onClick={() => {
                      const next = (item.cargos_salarios || []).filter((_, i) => i !== idx);
                      onChange('cargos_salarios', next);
                    }}
                  >🗑️</button>
                </div>
              </div>
            ))}
            <button 
              type="button" 
              className="btn-secundario lp-cta-sm" 
              style={{ marginTop: '5px' }}
              onClick={() => {
                const next = [...(item.cargos_salarios || []), { cargo: '', salario_base: 0 }];
                onChange('cargos_salarios', next);
              }}
            >
              + Adicionar Cargo
            </button>
          </div>

          <h4 className="form-secao-titulo">Gestão de Contrato e Integração SaaS</h4>
          <p className="texto-ajuda">
            O <strong>Status</strong> é alterado no fluxo de aprovação SaaS (não editável aqui).
          </p>
          <div className="linha-campos linha-2">
            <label>Status do Cliente
              <input value={String(item.status ?? 'pendente')} disabled readOnly />
            </label>
            <label>Plano Contratado
              <select value={item.plano_id || 0} onChange={(e) => onChange('plano_id', Number(e.target.value))}>
                <option value={0}>Selecione um plano...</option>
                <option value={1}>Bronze (Até 50 Alunos)</option>
                <option value={2}>Prata (Até 150 Alunos)</option>
                <option value={3}>Ouro (Ilimitado)</option>
              </select>
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
              <input 
                type="checkbox" 
                checked={item.contrato_assinado || false} 
                onChange={(e) => {
                  onChange('contrato_assinado', e.target.checked);
                  if (e.target.checked && !item.data_formalizacao) {
                    onChange('data_formalizacao', new Date().toISOString().split('T')[0]);
                  }
                }} 
              />
              Contrato Formalizado e Assinado
            </label>
            <label>Data de Formalização
              <input
                type="date" 
                value={item.data_formalizacao || ''} 
                onChange={(e) => onChange('data_formalizacao', e.target.value)}
                disabled={!item.contrato_assinado}
              />
            </label>
          </div>
          {item.status !== 'ativa' && item.contrato_assinado && (
            <div className="alerta-info" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e7f3ff', borderLeft: '4px solid #2196F3', borderRadius: '4px' }}>
              ℹ️ <strong>Nota:</strong> Marque a creche como <strong>ativa</strong> no fluxo de aprovação SaaS para liberar o acesso total ao sistema.
            </div>
          )}
        </>
      )}
    />
  );
}
